import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdChevronRight,
  MdClose,
  MdDescription,
  MdDownload,
  MdInsertDriveFile,
  MdOpenInNew,
  MdPictureAsPdf,
  MdSearch,
  MdTableChart,
} from 'react-icons/md';
import ExcelPreview from '../components/ExcelPreview';
import { useSetPageHeader } from '../contexts/PageHeaderContext';
import { addDownloadFile, getActiveUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';

interface DocumentItem {
  id: number;
  title: string;
  file_url: string;
  file_type: 'pdf' | 'excel' | string;
  created_at: string;
}

interface ApiResponse {
  code: number;
  status: boolean;
  message: string;
  data: Record<string, DocumentItem[]>;
}

const isPdf = (fileType: string) => fileType?.toLowerCase() === 'pdf';
const isExcel = (fileType: string) =>
  ['excel', 'xlsx', 'xls', 'csv'].includes(fileType?.toLowerCase());

const FileIcon = ({ fileType, size = 22 }: { fileType: string; size?: number }) => {
  if (isPdf(fileType)) return <MdPictureAsPdf size={size} className="text-red-500" />;
  if (isExcel(fileType)) return <MdTableChart size={size} className="text-green-600" />;
  return <MdInsertDriveFile size={size} className="text-slate-400" />;
};

const FileTypeBadge = ({ fileType }: { fileType: string }) => {
  if (isPdf(fileType))
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-500 border border-red-100">
        PDF
      </span>
    );
  if (isExcel(fileType))
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-100">
        Excel
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-400 border border-slate-100">
      File
    </span>
  );
};

const DocumentsPage: React.FC = () => {
  usePageMeta({
    title: 'Documents | HCAE Client Portal',
    description: 'Browse, preview, and download your documents from the HCAE Client Portal.',
  });
  useSetPageHeader('Documents', 'View and download your document logs');

  const user = getActiveUserInfo();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [documentsByCategory, setDocumentsByCategory] = useState<Record<string, DocumentItem[]>>({});
  const [activeTab, setActiveTab] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentItem | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  };

  useEffect(() => { fetchDocuments(debouncedSearch); }, [debouncedSearch]);
  useEffect(() => { setPreviewDoc(null); }, [activeTab]);
  useEffect(() => { setPreviewDoc(null); }, [debouncedSearch]);
  useEffect(() => { setPreviewLoaded(false); }, [previewDoc?.id]);

  const fetchDocuments = async (search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${BASE_URL}document/list${query}`, {
        headers: {
          'Content-Type': 'application/json',
          token: `${user?.api_token}`,
        },
      });
      const data: ApiResponse = await response.json();
      const categories = data?.data || {};
      setDocumentsByCategory(categories);
      const categoryKeys = Object.keys(categories);
      if (categoryKeys.length > 0) {
        setActiveTab((prev) => (categoryKeys.includes(prev) ? prev : categoryKeys[0]));
      } else {
        setActiveTab('');
      }
    } catch (error) {
      console.error('Document fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('en-GB');

  const handleDownload = async (doc: DocumentItem) => {
    try {
      const extension = isPdf(doc.file_type) ? 'pdf'
        : isExcel(doc.file_type) ? 'xlsx'
          : doc.file_type;

      const filename = `${doc.title}.${extension}`;
      const proxyUrl = `${BASE_URL}download-file?url=${encodeURIComponent(doc.file_url)}`;

      const response = await fetch(proxyUrl, {
        headers: { token: `${user?.api_token}` },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);

      addDownloadFile({
        id: String(doc.id),
        title: doc.title,
        url: doc.file_url,
        downloadedAt: new Date().toISOString(),
        file_type: isPdf(doc.file_type) ? 'pdf' : isExcel(doc.file_type) ? 'excel' : undefined,
      });
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback
      window.open(doc.file_url, '_blank', 'noopener,noreferrer');
    }
  };

  const tabs = useMemo(() => Object.keys(documentsByCategory), [documentsByCategory]);
  const currentDocs = documentsByCategory[activeTab] || [];

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-2xl p-4 border border-brand-100 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-brand-100 shrink-0" />
            <div className="flex-1">
              <div className="h-3.5 w-36 rounded-full bg-brand-100 mb-2" />
              <div className="h-2.5 w-20 rounded-full bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPreviewContent = (doc: DocumentItem) => {
    if (isPdf(doc.file_type)) {
      return (
        <div className="relative bg-slate-50 flex-1 overflow-hidden">
          {!previewLoaded && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="w-full h-full p-4 sm:p-6">
                <div className="h-full w-full rounded-xl bg-white border border-slate-100 shadow-sm animate-pulse" />
              </div>
            </div>
          )}
          <iframe
            src={`${doc.file_url}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full"
            style={{ height: 'min(70vh, calc(100vh - 360px))' }}
            title={doc.title}
            onLoad={() => setPreviewLoaded(true)}
          />
        </div>
      );
    }

    if (isExcel(doc.file_type)) {
      return (
        <ExcelPreview
          url={doc.file_url}
          title={doc.title}
          height="min(70vh, calc(100vh - 360px))"
        />
      );
    }

    return (
      <div
        className="flex items-center justify-center text-slate-400 text-sm bg-slate-50"
        style={{ height: 'min(70vh, calc(100vh - 360px))' }}
      >
        No preview available.
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2 bg-white border border-slate-100 rounded-2xl p-1 w-full sm:w-fit shadow-sm shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-2 sm:px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex-1 sm:flex-none ${activeTab === tab
                ? 'bg-brand-700 text-white shadow-md shadow-brand-200'
                : 'text-slate-500 hover:text-brand-700 hover:bg-brand-50'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:max-w-xs">
          <MdSearch size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search documents…"
            className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-2xl shadow-sm placeholder-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-300 transition"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
            >
              <MdClose size={15} />
            </button>
          )}
        </div>
      </div>

      {previewDoc ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-6">
          <div className="space-y-3">
            {currentDocs.map((doc) => (
              <button
                key={doc.id}
                onClick={() => setPreviewDoc(doc)}
                className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl border transition-all duration-200 ${previewDoc?.id === doc.id
                  ? 'border-brand-200 bg-brand-50 shadow-md'
                  : 'border-brand-100 bg-white hover:border-brand-200 hover:shadow-sm'
                  }`}
              >
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                  <FileIcon fileType={doc.file_type} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{doc.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <FileTypeBadge fileType={doc.file_type} />
                    <span className="text-xs text-slate-400">{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <MdChevronRight size={16} className="text-slate-300 shrink-0" />
              </button>
            ))}
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon fileType={previewDoc.file_type} size={18} />
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-800 text-sm truncate">{previewDoc.title}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <FileTypeBadge fileType={previewDoc.file_type} />
                    <p className="text-xs text-slate-400">{formatDate(previewDoc.created_at)}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 sm:justify-end shrink-0">
                <button
                  onClick={() => window.open(previewDoc.file_url, '_blank', 'noopener,noreferrer')}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-white text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-1 sm:flex-none"
                >
                  <MdOpenInNew size={16} />
                  Open
                </button>
                <button
                  onClick={() => handleDownload(previewDoc)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 text-white text-xs font-semibold rounded-xl transition-colors flex-1 sm:flex-none ${isExcel(previewDoc.file_type)
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-brand-700 hover:bg-brand-800'
                    }`}
                >
                  <MdDownload size={16} />
                  Download
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors shrink-0"
                >
                  <MdClose size={18} />
                </button>
              </div>
            </div>

            {renderPreviewContent(previewDoc)}
          </div>
        </div>
      ) : (
        <>
          {loading ? (
            renderSkeleton()
          ) : currentDocs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
              {currentDocs.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => setPreviewDoc(doc)}
                  className="bg-white rounded-2xl border border-brand-100 p-4 flex items-center gap-4 hover:shadow-md hover:border-brand-200 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-slate-100 transition-colors">
                    <FileIcon fileType={doc.file_type} size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-800 truncate">{doc.title}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <FileTypeBadge fileType={doc.file_type} />
                      <p className="text-xs text-slate-400">{formatDate(doc.created_at)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(doc);
                    }}
                    className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-all shrink-0 ${isExcel(doc.file_type)
                      ? 'border-green-100 text-green-600 hover:bg-green-600 hover:text-white hover:border-green-600'
                      : 'border-brand-100 text-brand-600 hover:bg-brand-600 hover:text-white hover:border-brand-600'
                      }`}
                  >
                    <MdDownload size={17} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
                <MdDescription size={30} />
              </div>
              <p className="text-slate-400 font-medium">No documents found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentsPage;